/* Phase 2.2C — Drag-and-drop CSV bulk import.
   Flow: upload → column mapping → server dry-run preview → per-row decisions
   → confirmed commit → row-level results + import history. */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Papa from "papaparse";
import { Loader2, Upload, Download, X, AlertTriangle, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { AdminGate, AdminShell, styles } from "@/pages/AdminWorkspace";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MAX_ROWS = 500;

const TARGET_FIELDS = [
  { key: "display_name", label: "Display name *" },
  { key: "legal_name", label: "Legal name" },
  { key: "categories", label: "Categories (comma-sep)" },
  { key: "service_areas", label: "Service areas (comma-sep)" },
  { key: "description", label: "Description" },
  { key: "website", label: "Website" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "tags", label: "Tags (comma-sep)" },
  { key: "organization_role", label: "Organization role" },
  { key: "visibility", label: "Visibility" },
];

const ACTION_CHIP = {
  created: "bg-green-50 text-green-800 border border-green-200",
  updated: "bg-gold/15 text-gold-deep border border-gold/40",
  skipped: "bg-navy/5 text-navy/60 border border-navy/15",
  error: "bg-red-50 text-red-800 border border-red-200",
};

export default function AdminImport() {
  const { authHeaders } = useUserAuth();
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const loadHistory = useCallback(async () => {
    const r = await axios.get(`${API}/admin/directory/import/history`, { headers: authHeaders() });
    setHistory(r.data.batches || []);
  }, [authHeaders]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const reset = () => {
    setStep(1); setFileName(""); setHeaders([]); setRows([]); setMapping({});
    setPreview(null); setDecisions({}); setResult(null); setErr("");
  };

  const handleFile = (file) => {
    setErr("");
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { setErr("Only .csv files are accepted."); return; }
    if (file.size > MAX_FILE_BYTES) { setErr("File exceeds the 2 MB limit."); return; }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        if (!res.data.length) { setErr("No data rows found in the file."); return; }
        if (res.data.length > MAX_ROWS) { setErr(`Maximum ${MAX_ROWS} rows per import.`); return; }
        setFileName(file.name);
        setHeaders(res.meta.fields || []);
        setRows(res.data);
        // Auto-guess mapping by exact header name
        const auto = {};
        (res.meta.fields || []).forEach((h) => {
          const key = h.trim().toLowerCase().replace(/\s+/g, "_");
          auto[h] = TARGET_FIELDS.some((f) => f.key === key) ? key : "";
        });
        setMapping(auto);
        setStep(2);
      },
      error: () => setErr("Could not parse the CSV file."),
    });
  };

  const mappedRows = () => rows.map((r) => {
    const out = {};
    Object.entries(mapping).forEach(([csvHeader, target]) => {
      if (target) out[target] = (r[csvHeader] ?? "").toString();
    });
    return out;
  });

  const runPreview = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/admin/directory/import/preview`, {
        filename: fileName, rows: mappedRows(),
      }, { headers: authHeaders() });
      setPreview(r.data);
      const d = {};
      r.data.rows.forEach((o) => {
        if (o.action === "created") d[o.row] = "create";
        else if (o.action === "updated") d[o.row] = "update";
        else d[o.row] = "skip";
      });
      setDecisions(d);
      setStep(3);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!window.confirm("Commit this import to the directory? This writes to the database and is audit-logged.")) return;
    setBusy(true); setErr("");
    try {
      const data = mappedRows();
      const payload = {
        filename: fileName,
        rows: preview.rows
          .filter((o) => o.action !== "error")
          .map((o) => ({ row: o.row, decision: decisions[o.row] || "skip", data: data[o.row - 1] })),
      };
      const r = await axios.post(`${API}/admin/directory/import/commit`, payload, { headers: authHeaders() });
      setResult(r.data);
      setStep(4);
      loadHistory();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    const r = await axios.get(`${API}/admin/directory/import/template`, { headers: authHeaders(), responseType: "text" });
    const blob = new Blob([r.data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "who-directory-import-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <AdminGate>
      <AdminShell title="CSV Bulk Import" testid="admin-import-page">
        <p className="mb-5 text-sm text-slateSoft max-w-3xl">
          Bulk-create or update directory records. Imports never set partnership, verification, or live-referral
          flags — every imported record lands as an <strong>unconfirmed demo record</strong>. Verified records are never
          overwritten.
        </p>
        {err && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="import-error">{err}</p>}

        {/* Step 1: upload */}
        {step === 1 && (
          <div className={`${styles.card} p-8`}>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${dragOver ? "border-gold bg-gold/5" : "border-borderSubtle"}`}
              data-testid="import-dropzone"
            >
              <Upload className="w-8 h-8 text-gold-deep mx-auto" />
              <p className="mt-3 font-display font-bold text-navy">Drag &amp; drop a CSV file here</p>
              <p className="mt-1 text-xs text-slateSoft">Max 2 MB · {MAX_ROWS} rows · .csv only</p>
              <div className="mt-4 flex justify-center gap-3">
                <button className={styles.btnGold} onClick={() => fileRef.current?.click()} data-testid="import-choose-file">Choose file</button>
                <button className={styles.btnGhost} onClick={downloadTemplate} data-testid="import-template"><Download className="w-3.5 h-3.5" /> Download template</button>
              </div>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="import-file-input" />
            </div>
          </div>
        )}

        {/* Step 2: mapping */}
        {step === 2 && (
          <div className={`${styles.card} p-6`} data-testid="import-mapping">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-navy text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-gold-deep" /> {fileName} — {rows.length} rows</h2>
              <button className={styles.btnGhost} onClick={reset}><X className="w-3.5 h-3.5" /> Start over</button>
            </div>
            <p className="mt-1 text-xs text-slateSoft">Map each CSV column to a directory field. Unmapped columns are ignored.</p>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {headers.map((h) => (
                <label key={h} className="flex items-center gap-3 text-sm">
                  <span className="w-40 truncate font-mono text-xs text-navy bg-cream-panel border border-borderSubtle rounded px-2 py-1.5">{h}</span>
                  <span className="text-slateSoft">→</span>
                  <select className={styles.input} value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`import-map-${h}`}>
                    <option value="">— ignore —</option>
                    {TARGET_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </label>
              ))}
            </div>
            {!Object.values(mapping).includes("display_name") && (
              <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Map a column to <strong>Display name</strong> to continue.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button className={styles.btnGhost} onClick={reset}>Cancel</button>
              <button className={styles.btnGold} disabled={busy || !Object.values(mapping).includes("display_name")} onClick={runPreview} data-testid="import-preview-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Run dry-run preview
              </button>
            </div>
          </div>
        )}

        {/* Step 3: preview + decisions */}
        {step === 3 && preview && (
          <div className={`${styles.card} p-6`} data-testid="import-preview">
            <h2 className="font-display font-bold text-navy text-lg">Dry-run preview — nothing written yet</h2>
            <div className="mt-3 flex flex-wrap gap-3 text-xs" data-testid="import-preview-counts">
              <span className={`${styles.chip} ${ACTION_CHIP.created}`}>{preview.counts.created} create</span>
              <span className={`${styles.chip} ${ACTION_CHIP.updated}`}>{preview.counts.updated} update</span>
              <span className={`${styles.chip} ${ACTION_CHIP.skipped}`}>{preview.counts.skipped} skip</span>
              <span className={`${styles.chip} ${ACTION_CHIP.error}`}>{preview.counts.errors} errors</span>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
                  <tr><th className="px-3 py-2 text-left">Row</th><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Result</th><th className="px-3 py-2 text-left">Detail</th><th className="px-3 py-2 text-left">Decision</th></tr>
                </thead>
                <tbody>
                  {preview.rows.map((o) => (
                    <tr key={o.row} className="border-b border-borderSubtle last:border-0" data-testid={`import-row-${o.row}`}>
                      <td className="px-3 py-2 text-xs text-slateSoft">{o.row}</td>
                      <td className="px-3 py-2 text-navy">{o.display_name || "—"}</td>
                      <td className="px-3 py-2"><span className={`${styles.chip} ${ACTION_CHIP[o.action]}`}>{o.action}</span></td>
                      <td className="px-3 py-2 text-xs text-slateSoft">{o.detail}</td>
                      <td className="px-3 py-2">
                        {o.action === "error" ? <span className="text-xs text-red-700">excluded</span> : (
                          <select className={`${styles.input} !py-1 text-xs`} value={decisions[o.row] || "skip"} onChange={(e) => setDecisions({ ...decisions, [o.row]: e.target.value })} data-testid={`import-decision-${o.row}`}>
                            {o.action === "created" && <option value="create">Create</option>}
                            {o.action === "updated" && <option value="update">Update</option>}
                            <option value="skip">Skip</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button className={styles.btnGhost} onClick={reset}>Cancel</button>
              <button className={styles.btnGold} disabled={busy} onClick={commit} data-testid="import-commit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Commit import
              </button>
            </div>
          </div>
        )}

        {/* Step 4: result */}
        {step === 4 && result && (
          <div className={`${styles.card} p-6`} data-testid="import-result">
            <h2 className="font-display font-bold text-navy text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-700" /> Import committed</h2>
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <span className={`${styles.chip} ${ACTION_CHIP.created}`}>{result.counts.created} created</span>
              <span className={`${styles.chip} ${ACTION_CHIP.updated}`}>{result.counts.updated} updated</span>
              <span className={`${styles.chip} ${ACTION_CHIP.skipped}`}>{result.counts.skipped} skipped</span>
              <span className={`${styles.chip} ${ACTION_CHIP.error}`}>{result.counts.errors} errors</span>
            </div>
            <button className={`${styles.btnGold} mt-5`} onClick={reset} data-testid="import-again">Import another file</button>
          </div>
        )}

        {/* History */}
        <section className="mt-10">
          <h2 className="font-display font-bold text-navy text-lg">Import history</h2>
          <div className={`${styles.card} mt-3 overflow-x-auto`} data-testid="import-history">
            <table className="w-full text-sm">
              <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
                <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">File</th><th className="px-3 py-2 text-left">Rows</th><th className="px-3 py-2 text-left">Outcome</th></tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id} className="border-b border-borderSubtle last:border-0" data-testid={`import-batch-${b.id}`}>
                    <td className="px-3 py-2 text-xs text-slateSoft whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-navy">{b.filename}</td>
                    <td className="px-3 py-2 text-xs text-slateSoft">{b.total_rows}</td>
                    <td className="px-3 py-2 text-xs text-slateSoft">{b.created} created · {b.updated} updated · {b.skipped} skipped · {b.errors} errors</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slateSoft">No imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </AdminShell>
    </AdminGate>
  );
}
